import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, File, X, AlertTriangle, CheckCircle, FileText, Car, Shield, Receipt, Camera } from 'lucide-react';

/**
 * Document types for vehicle entry workflow.
 * `label` reuses documents.types.* (canonical document type names);
 * `descriptionKey` is wizard-specific context text.
 */
const VEHICLE_DOCUMENT_TYPES = [
  { type: 'VEHICLE_REGISTRATION', icon: FileText, required: true, descriptionKey: 'registrationDescription' },
  { type: 'VEHICLE_INSURANCE_CARD', icon: Shield, required: false, descriptionKey: 'insuranceCardDescription' },
  { type: 'PURCHASE_INVOICE', icon: Receipt, required: false, descriptionKey: 'purchaseInvoiceDescription' },
  { type: 'LEASE_CONTRACT', icon: FileText, required: false, descriptionKey: 'leaseContractDescription' },
  { type: 'TECHNICAL_INSPECTION', icon: Car, required: false, descriptionKey: 'technicalInspectionDescription' },
  { type: 'VEHICLE_PHOTO', icon: Camera, required: false, descriptionKey: 'vehiclePhotoDescription' },
];

// Allowed file extensions
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.doc', '.docx'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

/**
 * StepDocuments - Document queue step for VehicleLifecycleWorkflow
 *
 * Documents are queued locally and uploaded after vehicle creation.
 * This step stores pending documents in formData.pendingDocuments array.
 */
function StepDocuments({ formData, updateFormData, isEditMode, pendingScanFile }) {
  const { t } = useTranslation();
  // Data attribute for guided experience spotlight
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Get pending documents from formData
  const pendingDocuments = formData.pendingDocuments || [];

  // Validate file
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: t('vehicles.wizard.documents.extensionNotAllowed', { ext }) };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t('vehicles.wizard.documents.fileTooLarge') };
    }
    return { valid: true };
  };

  // Handle file selection
  const handleFiles = useCallback((files) => {
    if (!selectedType) {
      setError(t('vehicles.wizard.documents.selectTypeFirst'));
      return;
    }
    setError('');

    const newDocs = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (validation.valid) {
        newDocs.push({
          id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file,
          fileName: file.name,
          fileSize: file.size,
          documentType: selectedType,
          documentLabel: selectedType ? t(`documents.types.${selectedType}`) : selectedType,
          status: 'pending'
        });
      } else {
        setError(validation.error);
      }
    }

    if (newDocs.length > 0) {
      updateFormData('pendingDocuments', [...pendingDocuments, ...newDocs]);
      setSelectedType('');
    }
  }, [selectedType, pendingDocuments, updateFormData, t]);

  // Remove document from queue
  const removeDocument = (docId) => {
    updateFormData('pendingDocuments', pendingDocuments.filter(d => d.id !== docId));
  };

  // Drag handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [handleFiles]);

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = '';
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Count documents by type
  const getDocCountForType = (type) => {
    let count = pendingDocuments.filter(d => d.documentType === type).length;
    if (type === 'VEHICLE_REGISTRATION' && pendingScanFile) count += 1;
    return count;
  };

  return (
    <div className="space-y-6" data-step="vehicle-documents">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.documents.title')}</h3>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">{t('vehicles.wizard.documents.recommendedTitle')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {isEditMode
                ? t('vehicles.wizard.documents.recommendedHintEdit')
                : t('vehicles.wizard.documents.recommendedHintCreate')}
            </p>
          </div>
        </div>
      </div>

      {/* Document Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('vehicles.wizard.documents.typeToAddLabel')}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {VEHICLE_DOCUMENT_TYPES.map(docType => {
            const count = getDocCountForType(docType.type);
            const Icon = docType.icon;
            const isScannedCarteGrise = docType.type === 'VEHICLE_REGISTRATION' && pendingScanFile;
            const docLabel = t(`documents.types.${docType.type}`);

            if (isScannedCarteGrise) {
              return (
                <div
                  key={docType.type}
                  className="p-3 border-2 border-green-500 bg-green-50 rounded-lg text-left cursor-default"
                >
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 text-green-600" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 truncate">
                          {docLabel}
                        </span>
                        <span className="bg-green-100 text-green-700 text-xs px-1.5 rounded-full">
                          {t('vehicles.wizard.documents.scannedBadge')}
                        </span>
                      </div>
                      <p className="text-xs text-green-700 truncate">{t('vehicles.wizard.documents.scannedAtIdentificationHint')}</p>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <button
                key={docType.type}
                type="button"
                onClick={() => setSelectedType(docType.type)}
                className={`p-3 border-2 rounded-lg text-left transition-all ${
                  selectedType === docType.type
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                }`}
              >
                <div className="flex items-start gap-2">
                  <Icon className={`w-5 h-5 flex-shrink-0 ${
                    selectedType === docType.type ? 'text-amber-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900 truncate">
                        {docLabel}
                      </span>
                      {docType.required && (
                        <span className="text-red-500 text-xs">*</span>
                      )}
                      {count > 0 && (
                        <span className="bg-green-100 text-green-700 text-xs px-1.5 rounded-full">
                          {count}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{t(`vehicles.wizard.documents.${docType.descriptionKey}`)}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Drop Zone */}
      {selectedType && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragging
              ? 'border-amber-500 bg-amber-50'
              : 'border-gray-300 hover:border-amber-400 hover:bg-gray-50'
            }
          `}
        >
          <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-amber-500' : 'text-gray-400'}`} />
          <p className="text-sm text-gray-600 mb-1">
            {t('vehicles.wizard.documents.dropHint', { type: t(`documents.types.${selectedType}`) })}
          </p>
          <p className="text-xs text-gray-400">
            {t('vehicles.wizard.documents.browseHint')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Queued Documents */}
      {pendingDocuments.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="font-medium text-gray-900 text-sm">
              {t('vehicles.wizard.documents.queuedTitle', { count: pendingDocuments.length })}
            </h4>
          </div>
          <div className="divide-y">
            {pendingDocuments.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                <File className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                      {doc.documentLabel}
                    </span>
                    <span>{formatSize(doc.fileSize)}</span>
                  </div>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <button
                  type="button"
                  onClick={() => removeDocument(doc.id)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Documents Message */}
      {pendingDocuments.length === 0 && !selectedType && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{t('vehicles.wizard.documents.noDocuments')}</p>
          <p className="text-xs mt-1">{t('vehicles.wizard.documents.selectTypeHint')}</p>
        </div>
      )}
    </div>
  );
}

export default StepDocuments;
